import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Compass, Heart, Sparkles } from "lucide-react";
import { LINKS } from "@/config/links";

export default function About() {
  return (
    <div data-testid="page-about">
      {/* HEADER */}
      <section className="relative pt-20 sm:pt-28 pb-12 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px] gradient-radial-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">About Linehaul Books</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white leading-[1.05]">
              A boutique firm with a <span className="text-[#FFCB05]">Midwest backbone</span>.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              We’re a small, modern bookkeeping firm built to serve the people who keep America moving. Truckers, owner-operators, small fleets, and authorities — clients who need a real partner, not a portal full of strangers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section className="relative py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="relative overflow-hidden rounded-2xl border border-white/5">
              <img
                src="https://images.pexels.com/photos/8297034/pexels-photo-8297034.jpeg"
                alt="Bookkeeper reviewing trucking financials"
                className="w-full h-[460px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#040914]/70 via-transparent to-transparent" />
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Our story</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">Built by people who learned the language of trucking.</h2>
            <div className="mt-6 space-y-4 text-[#9CA3AF] leading-relaxed">
              <p>
                Linehaul Books started with a simple observation: most bookkeeping firms don’t speak trucker. They send templates designed for ad agencies, treat IFTA like a chore they squeeze in at quarter-end, and treat owner-operators like an afterthought.
              </p>
              <p>
                We built the firm we’d want if we drove the truck — premium, technology-forward, and structured so we can actually keep up. That means clean QBO files, organized portals, async-friendly workflows, and a real human on the other end of every message.
              </p>
              <p>
                From the Midwest, we serve clients across the United States — from solo owner-operators running a single truck to small fleets running their own authority.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative py-20 sm:py-24 bg-[#0A111E] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">How we operate</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white max-w-2xl">Four principles. No exceptions.</h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: MapPin, t: "Trucker-first", d: "Every workflow, template, and report is built for OTR, owner-operators, and fleets — not retrofitted." },
              { icon: Compass, t: "Quiet & on time", d: "Bookkeeping should not be loud. Closes happen monthly. Filings happen on time. You don’t have to chase." },
              { icon: Heart, t: "Real relationships", d: "Boutique means you know us. A dedicated point of contact, with a team behind them — not a ticket queue." },
              { icon: Sparkles, t: "Modern stack", d: "QuickBooks Online, Financial Cents, Calendly, Loom. Tools that work the way you live — async-friendly." },
            ].map((v, i) => (
              <motion.div
                key={v.t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="p-7 rounded-2xl bg-[#040914] border border-white/5 hover:border-[#FFCB05]/30 transition-all"
              >
                <span className="inline-grid place-items-center h-10 w-10 rounded-xl bg-[#00274C] border border-white/10">
                  <v.icon className="h-5 w-5 text-[#FFCB05]" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-white">{v.t}</h3>
                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 sm:py-28">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#00274C] via-[#0A111E] to-[#040914] p-10 sm:p-14">
            <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#FFCB05]/15 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">Let’s talk.</h3>
                <p className="mt-3 text-[#9CA3AF]">Review the services, then send a quick inquiry &mdash; or grab a spot on our calendar.</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link
                  to="/services"
                  data-testid="about-get-started-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-all"
                >
                  Get Started <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={LINKS.discoveryCall}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="about-calendly-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
                >
                  See if Linehaul is a fit
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
